import alertState from '@/states/alertState';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import useGetHandsupInfo from '@/hooks/api/useGetHandsupInfo';
import { HandsUpInfoState, HandsUpInfoStateType } from '@/states/handsUpInfoState';
import { HandsUpInfoResponse } from '@/types/api/mypage/handsUpInfo';

import useSetLoading from '@/hooks/useSetLoading';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import Heading from '../atoms/Heading';
import HandsupPoReportInfoTable from './HandsupPoReportInfoTable';

/**
 * Hands-up 設備導入費の残債情報を取得し、金額概要と注意事項を表示するセクション。
 * 工事費残債と会員数依存の変動ルールを同じ場所で見せ、請求への誤解を減らす。
 */
const HandsupInfo = () => {
  const setAlert = useSetRecoilState(alertState);
  const { startLoading, finishLoading } = useSetLoading();

  const [handsUpInfo, setHandsUpInfo] = useRecoilState<HandsUpInfoStateType>(HandsUpInfoState);
  const { execute: getHandsup } = useGetHandsupInfo();

  useEffect(() => {
    callAPIGetHandsupInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Hands-up設備導入残債情報取得API呼出し */
  const callAPIGetHandsupInfo = async () => {
    try {
      startLoading(ApiUrl.Read.HANDSUP_INFO);
      const { data }: AxiosResponse<HandsUpInfoResponse, any> = await getHandsup();
      // 画面表示に必要な項目だけ Recoil へ反映し、他画面でも同じ残債情報を再利用できるようにする。
      setHandsUpInfo({
        ...handsUpInfo,
        total_prc_tax_include: data.total_prc_tax_include,
        instlmt_total_no: data.instlmt_total_no,
        instlmt_rmnd_no: data.instlmt_rmnd_no,
        instlmt_rmnd_prc: data.instlmt_rmnd_prc,
        member_count: data.member_count,
      });
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.HANDSUP_INFO);
    }
  };

  return (
    <div>
      <Heading as='h3'>Hands-up&emsp;設備導入残債</Heading>
      {/* API 未反映の期間は残債金額を出さず、表示開始タイミングだけを案内する。 */}
      {!handsUpInfo.total_prc_tax_include && (
        <p className='m-text'>設備導入費用の残債は、宅内工事が完了した翌月から表示されます。</p>
      )}

      {/* 金額取得後は概要表を表示し、詳細ルールは下の注意書きで補足する。 */}
      {handsUpInfo.total_prc_tax_include && (
        <div className='m-table'>
          <table>
            <colgroup>
              <col />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th className='m-table__th'>合計金額</th>
                <td className='m-table__td' data-testid='total_prc_tax_include'>
                  {`${handsUpInfo.total_prc_tax_include.toLocaleString()}円`}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>前々月の会員数</th>
                <td className='m-table__td' data-testid='member_count'>
                  {`${handsUpInfo.member_count.toLocaleString()}人`}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>残りのお支払い金額</th>
                <td className='m-table__td' data-testid='instlmt_rmnd_prc'>
                  {`${handsUpInfo.instlmt_rmnd_prc.toLocaleString()}円`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <ul className='m-list-notice'>
        <li className='m-list-notice__item'>
          Hands-up会費(設備導入工事費)はご利用マンション内でのNURO 光 for
          マンションを利用している契約者数に応じて
          金額が変動します。前月の月初１日時点での契約者が設備導入工事費お支払い対象者となります。
        </li>
        <li className='m-list-notice__item'>
          設備導入工事費には開通時点の税率で消費税が加算されております。開通後に消費税率が変更されても、設備導入工事費は開通時点の税率での分割払いとなります。
        </li>
        <li className='m-list-notice__item'>
          毎月お支払いいただくHands-up会費は、開通時期に関わらず請求対象月の税率で消費税が加算されます。
        </li>
      </ul>

      <HandsupPoReportInfoTable />
    </div>
  );
};

export default HandsupInfo;
